import { useCallback, useMemo, useRef, useEffect } from "react";
import { useTheme } from "next-themes";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { Compartment } from "@codemirror/state";
import { vim } from "@replit/codemirror-vim";
import {
  lightThemeExtensions,
  darkThemeExtensions,
} from "../shared/code-theme";

// Module-level compartments - shared across all instances
// These allow dynamic reconfiguration without destroying the editor
const themeCompartment = new Compartment();
const vimCompartment = new Compartment();

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minHeight?: string;
  placeholder?: string;
  vimEnabled?: boolean;
}

export function MarkdownEditor({
  value,
  onChange,
  disabled = false,
  minHeight = "400px",
  placeholder,
  vimEnabled = false,
}: MarkdownEditorProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  const handleChange = useCallback(
    (val: string) => {
      if (!disabled) {
        onChange(val);
      }
    },
    [onChange, disabled]
  );

  // Base extensions with compartment placeholders - only created once
  const extensions = useMemo(
    () => [
      vimCompartment.of(vimEnabled ? [vim()] : []),
      themeCompartment.of(isDark ? darkThemeExtensions : lightThemeExtensions),
      EditorView.lineWrapping,
    ],
    // Only recreate on initial mount - compartments handle updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Reconfigure theme compartment when theme changes
  useEffect(() => {
    const view = editorRef.current?.view;
    if (view) {
      view.dispatch({
        effects: themeCompartment.reconfigure(
          isDark ? darkThemeExtensions : lightThemeExtensions
        ),
      });
    }
  }, [isDark]);

  // Reconfigure vim compartment when vim mode changes
  useEffect(() => {
    const view = editorRef.current?.view;
    if (view) {
      view.dispatch({
        effects: vimCompartment.reconfigure(vimEnabled ? [vim()] : []),
      });
    }
  }, [vimEnabled]);

  return (
    <div className="rounded-lg border overflow-hidden" style={{ minHeight }}>
      <CodeMirror
        ref={editorRef}
        value={value}
        onChange={handleChange}
        extensions={extensions}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: false, // Not needed for markdown
          autocompletion: false, // Not needed for markdown
          rectangularSelection: true,
          crosshairCursor: false,
          highlightSelectionMatches: true,
          searchKeymap: true,
          tabSize: 2,
        }}
        style={{ minHeight }}
        editable={!disabled}
      />
    </div>
  );
}
